import React from 'react';
import { View } from 'react-native';

import tw from '@/styles/tailwind';
import Text from '@/components/Text';

// ───────────────────────────────────────────────────────────────────────────
// Reporte "Presupuestos" (por comunidad, por periodo: anual o trimestral).
// Tres partes:
//   1. Encabezado de cierre (conteos + ingreso ideal + saldos) — reusa métricas ya vistas.
//   2. Presupuesto por categoría → línea: Descripción/Alcances · Cant · Precio Unitario
//      · Sub Total (= Cant × P.U.) · Total (= Sub Total × 1.16 IVA). Subtotal por categoría.
//      Total de Egresos al final.
//   3. Firmas / autorizaciones.
//
// Mapeo backend:
//  • Presupuesto  → Vinte\CommunityBudget (vinte_community_budgets): community_id, year,
//      expense_type_id, monthly_budgets[]. ⇒ la CATEGORÍA = Vinte ExpenseType (jerárquico):
//      padre = categoría (Control de Acceso…), hijo = línea (Jardinería, Deshierbe…).
//      Esto confirma que el rubro de egresos Vinte debe ser ExpenseType, para comparar
//      presupuesto (CommunityBudget) vs gasto real (Expense) — alimenta la Balanza.
//  • Ingreso ideal = (conteo de viviendas) × cuota.
//  • Encabezado de cierre → Unit counts + FeeType.cuota + saldos (fondo de reserva, bancos).
// Pendiente: el detalle por línea (Cant / Precio Unitario / Alcances) es más rico que el
//  monthly_budgets actual → habrá que acondicionar CommunityBudget. Periodo anual vs trimestral.
// TODO(backend): reemplazar DEMO por useQuery(communityId, periodo).
// ───────────────────────────────────────────────────────────────────────────

const IVA = 0.16;

type Linea = { descripcion: string; cant: number; precioUnitario: number };
type Categoria = { nombre: string; lineas: Linea[] };

const COMMUNITY = 'Comunidad Real Bilbao A.C';
const PERIODO = 'Junio 2026';

// ─── Encabezado de cierre (DEMO) ─────────────────────────────────────────────
const CIERRE = {
  numeroTotalViviendas: 2683,
  viviendasActivas: 565,
  viviendasEntregadas: 435,
  privadasEnOperacion: 20,
  viviendasInventario: 130,
  parques: 3,
  local: 0,
  totalViviendasEntregadas: 565,
  cuota: 600,
  montoEnPasivo: 350000,
  cuotasAdelantadas: 87900,
  saldoInicialBancos: 49353.66,
  fondoDeReserva: 519937.74,
};

// ─── Presupuesto por categoría → línea (DEMO) ────────────────────────────────
const CATEGORIAS: Categoria[] = [
  {
    nombre: 'Control de Acceso',
    lineas: [
      { descripcion: 'Vigilancia Control de Acceso', cant: 4, precioUnitario: 17920.0 },
      { descripcion: 'Mantenimiento de equipo de control de acceso', cant: 1, precioUnitario: 21220.0 },
    ],
  },
  {
    nombre: 'Mantenimiento y Servicios de Conjunto',
    lineas: [
      { descripcion: 'Jardinería General de Conjunto', cant: 1, precioUnitario: 19837.69 },
      { descripcion: 'Deshierbe de Áreas de Conjunto', cant: 1, precioUnitario: 8074.9 },
      { descripcion: 'Barrido de conjunto', cant: 1, precioUnitario: 4319.06 },
      { descripcion: 'Recolección de Basura', cant: 13, precioUnitario: 500.0 },
    ],
  },
  {
    nombre: 'Conceptos de Mantto y Conservación de Privadas',
    lineas: [
      { descripcion: 'Jardinería Áreas comunes de Privada', cant: 1, precioUnitario: 9419.51 },
      { descripcion: 'Poda de Áreas de Estacionamiento', cant: 1, precioUnitario: 37191.24 },
      { descripcion: 'Barrido y Limpieza de Privada', cant: 1, precioUnitario: 4732.0 },
    ],
  },
  {
    nombre: 'Administración y Prestador de servicios',
    lineas: [
      { descripcion: 'Auxiliar Administrativo', cant: 1, precioUnitario: 18000.0 },
      { descripcion: 'Gastos Administrativos', cant: 1, precioUnitario: 9000.0 },
      { descripcion: 'Administrador residente', cant: 1, precioUnitario: 23900.0 },
    ],
  },
  {
    nombre: 'Gastos Administrativos de la AC',
    lineas: [
      { descripcion: 'Servicio contable, fiscal y tesorería central', cant: 1, precioUnitario: 4851.72 },
      { descripcion: 'Servicio de asesoría jurídica', cant: 1, precioUnitario: 4267.24 },
      { descripcion: 'Sistema de administración y QR (Ding-Dong)', cant: 1, precioUnitario: 4158.05 },
      { descripcion: 'Comisiones Bancarias', cant: 1, precioUnitario: 3500.0 },
      { descripcion: 'SAT', cant: 1, precioUnitario: 800.0 },
      { descripcion: 'Compras de Boyas para ingreso al desarrollo', cant: 150, precioUnitario: 145.75 },
    ],
  },
];

const FIRMAS = [
  { nombre: 'Nancy Pérez Vega', cargo: 'Representante de la A.C', accion: 'Autoriza' },
  { nombre: 'Uriel Levi Sánchez Barraza', cargo: 'Integración Social', accion: 'Vo.Bo' },
  { nombre: 'Rubi Ángeles Villedas', cargo: 'Prestador de servicios administrativos', accion: '' },
  { nombre: 'J. Diego Hernández Pineda', cargo: 'Coordinador de Postventa', accion: 'Vo.Bo' },
];

const money = (n: number) => n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });
const subtotalOf = (l: Linea) => l.cant * l.precioUnitario;
const totalOf = (l: Linea) => subtotalOf(l) * (1 + IVA);

// Fila label : valor del encabezado de cierre.
function DataRow({ label, value, strong }: { label: string; value: string | number; strong?: boolean }) {
  return (
    <View style={tw`row justify-between py-1.5 border-b border-gray-100`}>
      <Text style={tw`text-gray-500 text-xs`}>{label}</Text>
      <Text style={tw.style(`text-xs`, strong ? `text-secondary font-700` : `text-gray-800 font-500`)}>{value}</Text>
    </View>
  );
}

function NumCell({ children, w = 120, strong }: any) {
  return (
    <View style={tw.style(`px-2 py-2 items-end justify-center`, { width: w })}>
      <Text style={tw.style(`text-xs`, strong ? `text-gray-900 font-700` : `text-gray-700`)}>{children}</Text>
    </View>
  );
}

function BudgetCategory({ cat }: { cat: Categoria }) {
  const subSub = cat.lineas.reduce((a, l) => a + subtotalOf(l), 0);
  const subTot = cat.lineas.reduce((a, l) => a + totalOf(l), 0);

  return (
    <View style={tw`mb-4 border border-gray-100 rounded-xl overflow-hidden`}>
      {/* Banda de categoría */}
      <View style={tw`bg-secondary px-3 py-2`}>
        <Text style={tw`text-white font-600 text-sm`}>{cat.nombre}</Text>
      </View>

      {/* Header de columnas */}
      <View style={tw`row bg-secondary/10 border-b border-gray-200`}>
        <View style={tw`flex-1 px-3 py-2`}><Text style={tw`text-secondary font-600 text-xs`}>Descripción y/o alcances</Text></View>
        <NumCell w={56}><Text style={tw`text-secondary font-600 text-xs`}>Cant.</Text></NumCell>
        <NumCell><Text style={tw`text-secondary font-600 text-xs`}>Precio Unitario</Text></NumCell>
        <NumCell><Text style={tw`text-secondary font-600 text-xs`}>Sub Total</Text></NumCell>
        <NumCell><Text style={tw`text-secondary font-600 text-xs`}>Total</Text></NumCell>
      </View>

      {/* Líneas */}
      {cat.lineas.map((l, i) => (
        <View key={i} style={tw.style(`row border-b border-gray-100`, i % 2 === 1 && `bg-gray-50/40`)}>
          <View style={tw`flex-1 px-3 py-2 justify-center`}><Text style={tw`text-gray-700 text-xs`}>{l.descripcion}</Text></View>
          <NumCell w={56}>{l.cant.toFixed(2)}</NumCell>
          <NumCell>{money(l.precioUnitario)}</NumCell>
          <NumCell>{money(subtotalOf(l))}</NumCell>
          <NumCell>{money(totalOf(l))}</NumCell>
        </View>
      ))}

      {/* Subtotal */}
      <View style={tw`row bg-gray-50 border-t border-secondary/30`}>
        <View style={tw`flex-1 px-3 py-2 justify-center`}><Text style={tw`text-secondary font-700 text-xs`}>Subtotal servicios</Text></View>
        <NumCell w={56} />
        <NumCell />
        <NumCell strong>{money(subSub)}</NumCell>
        <NumCell strong>{money(subTot)}</NumCell>
      </View>
    </View>
  );
}

export default function Presupuestos({ communityId, date }: { communityId?: string; date?: Date }) {
  const grandSub = CATEGORIAS.reduce((a, c) => a + c.lineas.reduce((s, l) => s + subtotalOf(l), 0), 0);
  const grandTotal = CATEGORIAS.reduce((a, c) => a + c.lineas.reduce((s, l) => s + totalOf(l), 0), 0);

  return (
    <View style={tw`px-4 pb-10`}>
      <View style={tw`items-center mb-4`}>
        <Text style={tw`text-lg font-700 text-secondary`}>{COMMUNITY} — Presupuesto</Text>
        <Text style={tw`text-gray-400 text-xs`}>Datos al cierre del mes · {PERIODO}</Text>
      </View>

      {/* 1. Encabezado de cierre */}
      <View style={tw`row gap-x-4 mb-6`}>
        {/* Conteos */}
        <View style={tw`flex-1 bg-white rounded-xl border border-gray-100 px-4 py-3`}>
          <Text style={tw`text-secondary font-700 text-xs mb-2 uppercase`}>Datos al cierre</Text>
          <DataRow label="Número total de viviendas" value={CIERRE.numeroTotalViviendas} />
          <DataRow label="Viviendas activas" value={CIERRE.viviendasActivas} />
          <DataRow label="Viviendas entregadas" value={CIERRE.viviendasEntregadas} />
          <DataRow label="Privadas en operación" value={CIERRE.privadasEnOperacion} />
          <DataRow label="N° de viviendas inventario" value={CIERRE.viviendasInventario} />
          <DataRow label="Parques" value={CIERRE.parques} />
          <DataRow label="Local" value={CIERRE.local} />
          <DataRow label="Total viviendas entregadas" value={CIERRE.totalViviendasEntregadas} strong />
        </View>

        {/* Ingreso ideal + saldos */}
        <View style={tw`flex-1 bg-white rounded-xl border border-gray-100 px-4 py-3`}>
          <Text style={tw`text-secondary font-700 text-xs mb-2 uppercase`}>Ingreso ideal (× cuota {money(CIERRE.cuota)})</Text>
          <DataRow label="Total de viviendas" value={money(CIERRE.numeroTotalViviendas * CIERRE.cuota)} />
          <DataRow label="Viviendas activas" value={money(CIERRE.viviendasActivas * CIERRE.cuota)} />
          <DataRow label="Viviendas entregadas" value={money(CIERRE.viviendasEntregadas * CIERRE.cuota)} />
          <View style={tw`h-2`} />
          <DataRow label="Monto en pasivo" value={money(CIERRE.montoEnPasivo)} />
          <DataRow label="Cuotas adelantadas" value={money(CIERRE.cuotasAdelantadas)} />
          <DataRow label="Saldo inicial en bancos" value={money(CIERRE.saldoInicialBancos)} />
          <DataRow label="Fondo de reserva" value={money(CIERRE.fondoDeReserva)} strong />
        </View>
      </View>

      {/* 2. Presupuesto por categoría */}
      {CATEGORIAS.map(c => <BudgetCategory key={c.nombre} cat={c} />)}

      {/* Total de Egresos */}
      <View style={tw`row justify-end mb-6`}>
        <View style={tw`bg-secondary rounded-xl px-5 py-3 row items-center`}>
          <Text style={tw`text-white font-600 mr-6`}>Total de Egresos</Text>
          <Text style={tw`text-white/70 text-xs mr-3`}>{money(grandSub)}</Text>
          <Text style={tw`text-white font-700 text-base`}>{money(grandTotal)}</Text>
        </View>
      </View>

      {/* 3. Firmas */}
      <View style={tw`row flex-wrap gap-4 mt-2`}>
        {FIRMAS.map(f => (
          <View key={f.nombre} style={tw`w-[46%] border-t border-gray-300 pt-2`}>
            <Text style={tw`text-gray-800 font-600 text-xs`}>{f.nombre}</Text>
            <Text style={tw`text-gray-500 text-xs`}>{f.cargo}</Text>
            {!!f.accion && <Text style={tw`text-gray-400 text-xs mt-0.5`}>{f.accion}</Text>}
          </View>
        ))}
      </View>

      {/* <Text style={tw`text-gray-300 text-xs mt-6`}>
        Datos demo · Total = Sub Total × 1.16 (IVA) · presupuesto = CommunityBudget (por definir granularidad) · communityId: {communityId ?? '—'}
      </Text> */}
    </View>
  );
}
